export interface ICD10Code {
  code: string;
  description: string;
  clinical_disease?: string;
}

// Common ICD-10 codes for quick selection during approval
export const COMMON_ICD10_CODES: ICD10Code[] = [
  { code: 'I10', description: 'Essential (primary) hypertension' },
  { code: 'I11.9', description: 'Hypertensive heart disease without heart failure' },
  { code: 'E78.5', description: 'Hyperlipidemia, unspecified' },
  { code: 'E78.0', description: 'Pure hypercholesterolemia, unspecified' },
  { code: 'E78.1', description: 'Pure hyperglyceridemia' },
  { code: 'E78.2', description: 'Mixed hyperlipidemia' },
  { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
  { code: 'E11.65', description: 'Type 2 diabetes mellitus with hyperglycemia' },
  { code: 'E11.22', description: 'Type 2 diabetes mellitus with diabetic chronic kidney disease' },
  { code: 'E11.40', description: 'Type 2 diabetes mellitus with diabetic neuropathy, unspecified' },
  { code: 'J44.9', description: 'Chronic obstructive pulmonary disease, unspecified' },
  { code: 'J44.1', description: 'Chronic obstructive pulmonary disease with acute exacerbation' },
  { code: 'I25.10', description: 'Atherosclerotic heart disease of native coronary artery' },
  { code: 'N18.3', description: 'Chronic kidney disease, stage 3 (moderate)' },
  { code: 'N18.4', description: 'Chronic kidney disease, stage 4 (severe)' },
  { code: 'I50.9', description: 'Heart failure, unspecified' },
  { code: 'I48.91', description: 'Unspecified atrial fibrillation' },
  { code: 'E66.9', description: 'Obesity, unspecified' },
  { code: 'E66.01', description: 'Morbid (severe) obesity due to excess calories' },
  { code: 'J45.909', description: 'Unspecified asthma, uncomplicated' },
  { code: 'G47.33', description: 'Obstructive sleep apnea' },
  { code: 'M10.9', description: 'Gout, unspecified' },
  { code: 'K21.0', description: 'Gastro-esophageal reflux disease with esophagitis' },
  { code: 'F32.9', description: 'Major depressive disorder, single episode, unspecified' },
  { code: 'F41.1', description: 'Generalized anxiety disorder' },
  { code: 'M54.5', description: 'Low back pain' },
  { code: 'E03.9', description: 'Hypothyroidism, unspecified' },
];

// Default ICD-10 codes per disease — used as fallback when LLM doesn't return them,
// and as suggestion list during approval editing. Keys are lowercase for matching.
export const DISEASE_ICD10_MAP: Record<string, ICD10Code[]> = {
  'hypertension': [
    { code: 'I10', description: 'Essential (primary) hypertension' },
  ],
  'hypertensive heart disease': [
    { code: 'I11.9', description: 'Hypertensive heart disease without heart failure' },
  ],
  'mixed hyperlipidemia': [
    { code: 'E78.2', description: 'Mixed hyperlipidemia' },
  ],
  'hyperlipidemia unspecified': [
    { code: 'E78.5', description: 'Hyperlipidemia, unspecified' },
  ],
  'hyperlipidemia': [
    { code: 'E78.5', description: 'Hyperlipidemia, unspecified' },
    { code: 'E78.2', description: 'Mixed hyperlipidemia' },
  ],
  'hypercholesterolemia': [
    { code: 'E78.0', description: 'Pure hypercholesterolemia, unspecified' },
  ],
  'diabetes with hyperglycemia': [
    { code: 'E11.65', description: 'Type 2 diabetes mellitus with hyperglycemia' },
  ],
  'diabetes without complications': [
    { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
  ],
  'type 2 diabetes mellitus': [
    { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
    { code: 'E11.65', description: 'Type 2 diabetes mellitus with hyperglycemia' },
  ],
  'type 2 diabetes': [
    { code: 'E11.9', description: 'Type 2 diabetes mellitus without complications' },
    { code: 'E11.65', description: 'Type 2 diabetes mellitus with hyperglycemia' },
  ],
  'copd': [
    { code: 'J44.9', description: 'Chronic obstructive pulmonary disease, unspecified' },
  ],
  'chronic obstructive pulmonary disease': [
    { code: 'J44.9', description: 'Chronic obstructive pulmonary disease, unspecified' },
  ],
  'coronary artery disease': [
    { code: 'I25.10', description: 'Atherosclerotic heart disease of native coronary artery' },
  ],
  'atherosclerotic heart disease': [
    { code: 'I25.10', description: 'Atherosclerotic heart disease of native coronary artery' },
  ],
  'chronic kidney disease': [
    { code: 'N18.3', description: 'Chronic kidney disease, stage 3 (moderate)' },
  ],
  'heart failure': [
    { code: 'I50.9', description: 'Heart failure, unspecified' },
  ],
  'congestive heart failure': [
    { code: 'I50.9', description: 'Heart failure, unspecified' },
  ],
  'atrial fibrillation': [
    { code: 'I48.91', description: 'Unspecified atrial fibrillation' },
  ],
  'obesity': [
    { code: 'E66.9', description: 'Obesity, unspecified' },
  ],
  'asthma': [
    { code: 'J45.909', description: 'Unspecified asthma, uncomplicated' },
  ],
  'obstructive sleep apnea': [
    { code: 'G47.33', description: 'Obstructive sleep apnea' },
  ],
  'sleep apnea': [
    { code: 'G47.33', description: 'Obstructive sleep apnea' },
  ],
  'gout': [
    { code: 'M10.9', description: 'Gout, unspecified' },
  ],
  'gastroesophageal reflux': [
    { code: 'K21.9', description: 'Gastro-esophageal reflux disease without esophagitis' },
  ],
  'gerd': [
    { code: 'K21.9', description: 'Gastro-esophageal reflux disease without esophagitis' },
  ],
  'depression': [
    { code: 'F32.9', description: 'Major depressive disorder, single episode, unspecified' },
  ],
  'anxiety': [
    { code: 'F41.1', description: 'Generalized anxiety disorder' },
  ],
  'hypothyroidism': [
    { code: 'E03.9', description: 'Hypothyroidism, unspecified' },
  ],
  'benign prostatic hyperplasia': [
    { code: 'N40.1', description: 'Benign prostatic hyperplasia with lower urinary tract symptoms' },
  ],
  'bph': [
    { code: 'N40.1', description: 'Benign prostatic hyperplasia with lower urinary tract symptoms' },
  ],
  'enlarged prostate': [
    { code: 'N40.1', description: 'Benign prostatic hyperplasia with lower urinary tract symptoms' },
  ],
  'osteoporosis': [
    { code: 'M81.0', description: 'Age-related osteoporosis without current pathological fracture' },
  ],
  'ischemic heart disease': [
    { code: 'I25.10', description: 'Atherosclerotic heart disease of native coronary artery without angina pectoris' },
  ],
  "alzheimer's disease": [
    { code: 'G30.9', description: "Alzheimer's disease, unspecified" },
  ],
  'alzheimer': [
    { code: 'G30.9', description: "Alzheimer's disease, unspecified" },
  ],
  'dementia': [
    { code: 'F03.90', description: 'Unspecified dementia, unspecified severity, without behavioral disturbance, psychotic disturbance, mood disturbance, and anxiety' },
  ],
  // PHA-1219: plain "Anemia" is its own key so it is suggested D64.9, not the
  // iron-deficiency D50.9 that 'iron deficiency anemia' would otherwise supply.
  'anemia': [
    { code: 'D64.9', description: 'Anemia, unspecified' },
    { code: 'D50.9', description: 'Iron deficiency anemia, unspecified' },
    { code: 'D63.1', description: 'Anemia in chronic kidney disease' },
    { code: 'D63.8', description: 'Anemia in other chronic diseases classified elsewhere' },
  ],
  'iron deficiency anemia': [
    { code: 'D50.9', description: 'Iron deficiency anemia, unspecified' },
  ],
  'low back pain': [
    { code: 'M54.5', description: 'Low back pain' },
  ],
};

// Patterns for the 3 diseases that require ICD-10 codes for care plan generation.
// Mirrors _CARE_PLAN_DISEASE_PATTERNS in the backend (ccm/app.py).
const SUPPORTED_DISEASE_PATTERNS: RegExp[] = [
  /hypertens/i,
  /hyperlipidemia|dyslipidemia/i,
  /diabetes|diabetic/i,
];

/**
 * Returns true if the problem name matches one of the 3 supported diseases
 * (Hypertension, Hyperlipidemia, Diabetes) that require ICD-10 codes.
 */
export const isSupportedDisease = (problemName: string): boolean =>
  SUPPORTED_DISEASE_PATTERNS.some(pattern => pattern.test(problemName));

/**
 * Get suggested ICD-10 codes for a given problem name by fuzzy matching
 * against the DISEASE_ICD10_MAP keys.
 */
export const getSuggestedICD10Codes = (problemName: string): ICD10Code[] => {
  const lower = problemName.toLowerCase();
  // Try exact key match first
  if (DISEASE_ICD10_MAP[lower]) {
    return DISEASE_ICD10_MAP[lower];
  }
  // Find all matching keys, prefer the most specific (longest) match
  const matchedKeys = Object.keys(DISEASE_ICD10_MAP).filter(
    k => lower.includes(k) || k.includes(lower)
  );
  if (matchedKeys.length === 0) return [];
  matchedKeys.sort((a, b) => b.length - a.length);
  return DISEASE_ICD10_MAP[matchedKeys[0]];
};
